import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CaretUp, GearSix, Question, SignOut } from '@phosphor-icons/react';
import { authClient } from '../../lib/api';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { useSession } from '../../lib/session';
import { isStaffRoleName, ROLE_LABEL } from '../../lib/staff-nav';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { Popover } from '../ui/popover';
import { StatusTag } from '../ui/status-tag';

const MENU_ITEM =
  'flex w-full items-center gap-(--mbm-space-3) px-(--mbm-space-4) py-(--mbm-space-3) text-label-medium text-text-primary hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

const COMING_SOON =
  'flex w-full cursor-not-allowed items-center gap-(--mbm-space-3) px-(--mbm-space-4) py-(--mbm-space-3) text-label-medium text-text-tertiary';

/** The avatar at the foot of the rail: name and role beside it when the rail is open, an account menu on click. */
export function ProfileMenu({ collapsed }: { collapsed: boolean }) {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = isStaffRoleName(session?.user.role) ? session.user.role : undefined;

  const signOut = async () => {
    await authClient.signOut();
    queryClient.clear();
    await navigate({ to: '/subdomain/admin/admin-login' });
  };

  return (
    <Popover
      role="menu"
      placement="top-start"
      className="w-(--mbm-layout-menu-width)"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label="Account menu"
          className="flex w-full items-center gap-(--mbm-space-3) overflow-hidden rounded-(--mbm-radius-sm) p-(--mbm-space-1) text-left hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Avatar
            initials={initialsOf(session?.user.name, session?.user.email)}
            size="sm"
            className="bg-brand-primary"
          />
          <span
            className={cx(
              'flex min-w-0 flex-1 flex-col transition-opacity duration-(--mbm-duration-fast)',
              collapsed && 'opacity-0',
            )}
          >
            <span className="truncate text-label-medium text-text-primary">{session?.user.name}</span>
            <span className="truncate text-body-xsmall text-text-tertiary">{role ? ROLE_LABEL[role] : ''}</span>
          </span>
          <span
            className={cx(
              'text-text-tertiary transition-opacity duration-(--mbm-duration-fast)',
              collapsed && 'opacity-0',
            )}
          >
            <Icon icon={CaretUp} size="sm" />
          </span>
        </button>
      )}
    >
      {() => (
        <div className="flex flex-col py-(--mbm-space-1)">
          <div className="flex items-center gap-(--mbm-space-3) border-b border-border-line px-(--mbm-space-4) pb-(--mbm-space-3) pt-(--mbm-space-3)">
            <Avatar
              initials={initialsOf(session?.user.name, session?.user.email)}
              size="md"
              className="bg-brand-primary"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-label-medium text-text-primary">{session?.user.name}</span>
              <span className="truncate text-body-xsmall text-text-tertiary">{session?.user.email}</span>
            </div>
            {role ? <StatusTag label={ROLE_LABEL[role]} tone="blue" /> : null}
          </div>
          <Link to="/subdomain/admin/settings/profile" role="menuitem" className={MENU_ITEM}>
            <Icon icon={GearSix} size="md" />
            My settings
          </Link>
          <Link to="/subdomain/admin/settings/notifications" role="menuitem" className={MENU_ITEM}>
            <Icon icon={Bell} size="md" />
            Notification settings
          </Link>
          <span role="menuitem" aria-disabled="true" title="Coming soon" className={COMING_SOON}>
            <Icon icon={Question} size="md" />
            Help & shortcuts
          </span>
          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-(--mbm-space-3) border-t border-border-line px-(--mbm-space-4) py-(--mbm-space-3) text-left text-label-medium text-feedback-error hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={SignOut} size="md" />
            Sign out
          </button>
        </div>
      )}
    </Popover>
  );
}
